import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import Container from "@cloudscape-design/components/container"
import Form from "@cloudscape-design/components/form"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Input from "@cloudscape-design/components/input"
import Link from "@cloudscape-design/components/link"
import Popover from "@cloudscape-design/components/popover"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useEffect, useState, type FormEvent, type ReactNode } from "react"
import { useSearchParams } from "react-router-dom"
import { getConfig } from "../../config"
import { useAuth } from "../../contexts/AuthContext"
import { REDIRECT_PARAM, storeRedirectTarget } from "../../utils/redirect"
import CdrLogo from "./CdrLogo"
import CoverPhoto from "./CoverPhoto"
import "./Login.css"

const Login = () => {
  // Stash the deep-link target (from an email link or ProtectedRoute) so it survives the sign-in
  // flow, including the federated/direct-oidc round trip through the IDP that drops the query string.
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get(REDIRECT_PARAM)
  useEffect(() => {
    storeRedirectTarget(redirect)
  }, [redirect])

  // Native is the only password mode; every other mode is a redirect/SSO flow and shares the
  // SSO-button screen. New redirect modes need no change here.
  if (getConfig().authMode === "native") {
    return <NativeLogin />
  }
  return <RedirectLogin />
}

/**
 * Shared split-panel login layout: branded form panel on the left, cover art on the right. Both
 * native and federated modes render through this - only the inner sign-in content (SSO button vs.
 * username/password form) differs.
 */
const LoginLayout = ({ children, footer }: { children: ReactNode; footer?: ReactNode }) => (
  <div className="login-page">
    <div className="login-split">
      <div className="login-form-panel">
        <div className="login-form-content">
          <SpaceBetween size="l">
            <CdrLogo />

            <Container header={<Header variant="h2">Sign in</Header>} footer={footer}>
              {children}
            </Container>

            <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
              Clinical Data Repository
            </Box>
          </SpaceBetween>
        </div>
      </div>

      <div className="login-cover-panel">
        <CoverPhoto />
      </div>
    </div>
  </div>
)

/**
 * SSO sign-in screen for the redirect-based modes (federated and direct-oidc). The button kicks off
 * loginWithRedirect, which AuthContext routes to the mode-appropriate IDP flow.
 */
const RedirectLogin = () => {
  const { loginWithRedirect } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSsoLogin = async () => {
    setError(null)
    setLoading(true)
    try {
      await loginWithRedirect()
    } catch (err: unknown) {
      console.error(err)
      setError("Failed to initiate sign-in. Please try again.")
      setLoading(false)
    }
  }

  return (
    <LoginLayout>
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" i18nStrings={{ errorIconAriaLabel: "Error" }}>
            {error}
          </Alert>
        )}
        <Button variant="primary" onClick={() => void handleSsoLogin()} loading={loading} fullWidth>
          Sign in with SSO
        </Button>
      </SpaceBetween>
    </LoginLayout>
  )
}

const NativeLogin = () => {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await login(username, password)
    } catch (err: unknown) {
      console.error(err)
      setError("Login failed. Please check your credentials.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <LoginLayout
      footer={
        <Button variant="primary" formAction="submit" form="login-form" loading={loading} fullWidth>
          {loading ? "Signing in..." : "Sign In"}
        </Button>
      }
    >
      <form id="login-form" onSubmit={(e) => void handleSubmit(e)}>
        <Form>
          <SpaceBetween size="l">
            {error && (
              <Alert type="error" i18nStrings={{ errorIconAriaLabel: "Error" }}>
                {error}
              </Alert>
            )}

            <FormField label="Email or Username">
              <Input
                value={username}
                onChange={({ detail }) => setUsername(detail.value)}
                autoComplete
                autoFocus
                controlId="username"
                placeholder="Enter your email or username"
              />
            </FormField>

            <FormField label="Password">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={({ detail }) => setPassword(detail.value)}
                autoComplete
                controlId="password"
                placeholder="Enter your password"
              />
            </FormField>

            <Checkbox
              checked={showPassword}
              onChange={({ detail }) => setShowPassword(detail.checked)}
            >
              Show password
            </Checkbox>

            <Popover
              triggerType="custom"
              header="Password reset"
              content="Passwords are managed by your organization's authentication system. Contact your team administrator to reset your password."
              dismissAriaLabel="Close"
            >
              <Link variant="primary">Forgot your password?</Link>
            </Popover>
          </SpaceBetween>
        </Form>
      </form>
    </LoginLayout>
  )
}

export default Login
